import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";

import { useDashboard } from "@/stores/dashboard/dashboard.store";

/**
 * Project-scoped accessor for the Overview-selected indicator-type-mapping ("unit").
 *
 * Prefers the live dashboard store value (reactive when the user changes it in the Overview),
 * and falls back to the persisted localStorage value for deep-links that never opened the
 * Overview. Returns `null` when nothing is selected — callers should send `undefined` so the
 * backend resolves its own default.
 *
 * This lets non-dashboard surfaces (Transport, Waste Sorting) follow the same unit selection as
 * the Overview without depending on the dashboard store being initialised.
 */
export function useSelectedIndicatorMapping(projectId: Ref<string | undefined>) {
  const { selectedIndicatorTypeMapping } = storeToRefs(useDashboard());

  const readPersisted = (id: string | undefined): string | null => {
    if (!id) return null;
    try {
      const raw = localStorage.getItem(`gaiup-${id}-dashboard-selectedIndicatorTypeMapping`);
      return raw ? (JSON.parse(raw) as string | null) : null;
    } catch {
      // corrupted value — fall back to default
      return null;
    }
  };

  const selectedMappingId = computed<string | null>(() => selectedIndicatorTypeMapping.value ?? readPersisted(projectId.value));

  return { selectedMappingId };
}
